/**
 * Put the cursor on the first field a form is complaining about.
 *
 * Client-side validation used to return early and nothing else: the errors rendered next to the
 * fields, and on a phone the field in question was often two screens up, so tapping the button
 * looked like it had done nothing at all. Focus answers that, and it is also what a screen reader
 * needs — the refusal is announced by `aria-describedby` on the field it belongs to.
 *
 * **In field order, not error order.** The caller passes the order the fields are read in, so the
 * cursor lands on the earliest problem rather than on whichever check happened to run last.
 *
 * Shared because two screens need it and it is the kind of four lines that goes subtly different
 * in the second copy. Lives here rather than in `api/`, because that layer knows nothing about a
 * DOM.
 */
export function focusFirstError(
  root: HTMLElement | null | undefined,
  order: readonly string[],
  errors: Readonly<Record<string, string | undefined>>,
): void {
  if (!root) return;

  const first = order.find((field) => errors[field]);
  if (!first) return;

  const field = root.querySelector<HTMLElement>(`[name="${first}"]`);
  if (!field) return;

  field.focus();
  /*
   * Optional-called: jsdom does not implement it, and a helper that throws in every unit test
   * that submits an invalid form would be a helper nobody keeps. `block: 'center'` rather than
   * the default, because a field scrolled to the very top of the viewport sits under the header.
   */
  field.scrollIntoView?.({ block: 'center' });
}
